// import { useTranslation } from 'react-i18next';

/**
 * Тип для функции форматирования
 */
type FormatSecondsMethod = (totalSeconds: number) => string;

/**
 * Хук для форматирования общего количества секунд в строку продолжительности.
 * Формат: ЧЧ:ММ:СС, ММ:СС или СС, с учетом локализации (i18n).
 *
 * @returns {FormatSecondsMethod} Функция, которая принимает totalSeconds (число)
 * и возвращает отформатированную строку (string).
 */
export const useFormatSeconds = () => {
  // Получаем функцию t и текущий язык (i18n) из react-i18next
  // const { t } = useTranslation();

  // Локализованные ключи
  // const separator = t('time_separator', { defaultValue: ':' }); // Разделитель времени (например, ':', '.')
  // const unitH = t('time_unit_h', { defaultValue: null }); // Единица для часов (например, 'ч', 'h')
  // const unitM = t('time_unit_m', { defaultValue: null }); // Единица для минут (например, 'м', 'min')
  // const unitS =  t('time_unit_s', { defaultValue: null }); // Единица для секунд (например, 'с', 'sec')
  const separator = ':';
  const unitH = '';
  const unitM = '';
  const unitS = '';

  // Определяем, какой формат использовать:
  // 1. Формат "С единицами" (1ч 30м 15с), если все единицы времени присутствуют.
  // 2. Формат "Цифровой" (1:30:15) в противном случае.
  const isUnitFormat = !!(unitH && unitM && unitS);

  /**
   * Принимает общее количество секунд и форматирует его в строку.
   *
   * @param {number} totalSeconds Общее количество секунд для форматирования.
   * @returns {string} Отформатированная строка продолжительности.
   */
  const formatSeconds: FormatSecondsMethod = (totalSeconds: number): string => {
    // Убедимся, что мы работаем с положительным числом
    const seconds = Math.max(0, Math.floor(totalSeconds));

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;

    // ---------------------------------------------
    // 1. Формат "С единицами" (1ч 30м 15с)
    // ---------------------------------------------
    if (isUnitFormat) {
      if (hours > 0) {
        // ЧЧ ММ СС (всегда отображаются все компоненты, если есть часы)
        return `${hours}${unitH} ${minutes}${unitM} ${remainingSeconds}${unitS}`;
      } else if (minutes > 0) {
        // ММ СС
        return `${minutes}${unitM} ${remainingSeconds}${unitS}`;
      } else {
        // СС
        return `${remainingSeconds}${unitS}`;
      }
    }

    // ---------------------------------------------
    // 2. Формат "Цифровой" (ЧЧ:ММ:СС или ММ:СС или СС)
    // ---------------------------------------------
    else {
      // Функция для добавления ведущего нуля
      const pad = (num: number): string => num.toString().padStart(2, '0');

      if (hours > 0) {
        // ЧЧ:ММ:СС (всегда отображаются все компоненты, если есть часы)
        return `${hours}${separator}${pad(minutes)}${separator}${pad(remainingSeconds)}`;
      } else return `${minutes}${separator}${pad(remainingSeconds)}`;
    }
  };

  return { formatSeconds };
};
